import { sql } from "drizzle-orm";

export type WorkflowV2DbLike = {
  execute: (query: ReturnType<typeof sql>) => Promise<unknown>;
  transaction?: <T>(callback: (tx: WorkflowV2DbLike) => Promise<T>) => Promise<T>;
};

const TRANSIENT_DB_CODES = new Set([
  "08000",
  "08001",
  "08003",
  "08004",
  "08006",
  "08P01",
  "40001",
  "40P01",
  "53300",
  "55P03",
  "57014",
  "57P01",
  "57P02",
  "57P03",
]);

const TRANSIENT_DB_MESSAGE_PATTERNS = [
  "failed query:",
  "socket connection was closed unexpectedly",
  "unable to connect",
  "connection terminated",
  "connection reset",
  "server closed the connection",
  "server was not ready",
  "client disconnect while server was not ready",
  "econnreset",
  "etimedout",
  "timeout",
];

const MAX_ERROR_CAUSE_DEPTH = 8;

export function collectWorkflowV2ErrorCodes(
  error: unknown,
  codes: string[] = [],
  seen = new Set<unknown>(),
  depth = 0,
): string[] {
  if (!error || typeof error !== "object" || seen.has(error) || depth > MAX_ERROR_CAUSE_DEPTH) return codes;
  seen.add(error);
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && code) codes.push(code);
  const cause = (error as { cause?: unknown }).cause;
  if (cause && cause !== error) collectWorkflowV2ErrorCodes(cause, codes, seen, depth + 1);
  return codes;
}

export function collectWorkflowV2ErrorMessages(
  error: unknown,
  messages: string[] = [],
  seen = new Set<unknown>(),
  depth = 0,
): string[] {
  if (depth > MAX_ERROR_CAUSE_DEPTH) return messages;
  if (error instanceof Error) {
    messages.push(error.message);
    if (!seen.has(error)) {
      seen.add(error);
      if (error.cause && error.cause !== error) {
        collectWorkflowV2ErrorMessages(error.cause, messages, seen, depth + 1);
      }
    }
    return messages;
  }
  if (typeof error === "string") {
    messages.push(error);
    return messages;
  }
  if (error && typeof error === "object") {
    if (seen.has(error)) return messages;
    seen.add(error);
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") messages.push(message);
    const cause = (error as { cause?: unknown }).cause;
    if (cause && cause !== error) collectWorkflowV2ErrorMessages(cause, messages, seen, depth + 1);
  }
  return messages;
}

export function isTransientWorkflowV2DbError(error: unknown): boolean {
  if (collectWorkflowV2ErrorCodes(error).some((code) => TRANSIENT_DB_CODES.has(code))) return true;
  const message = collectWorkflowV2ErrorMessages(error).join("\n").toLowerCase();
  return TRANSIENT_DB_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function executeWorkflowV2Query(
  db: WorkflowV2DbLike,
  query: ReturnType<typeof sql>,
  options: { attempts?: number; baseDelayMs?: number } = {},
): Promise<unknown> {
  const attempts = Math.max(1, options.attempts ?? 3);
  const baseDelayMs = Math.max(0, options.baseDelayMs ?? 100);
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await db.execute(query);
    } catch (error) {
      lastError = error;
      if (attempt >= attempts || !isTransientWorkflowV2DbError(error)) {
        throw error;
      }
      await sleep(baseDelayMs * 2 ** (attempt - 1));
    }
  }

  throw lastError;
}
